import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddIcon from '@mui/icons-material/Add';
import PaymentDrawerHeaderTitle from '../booking/PaymentDrawerHeaderTitle';
import BookingOverpaidBanner from '../booking/BookingOverpaidBanner';
import BookingAddPaymentDrawer from '../booking/BookingAddPaymentDrawer';
import BookingAuditDrawer from '../booking/BookingAuditDrawer';
import { getPaymentList, updateBooking } from '../../services/api';
import { detectThemeSync } from '../../utils/themeUtils';
import { formatHotelDateTime } from '../../utils/dateUtils';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerHeaderSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
  getBookingDrawerFooterSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  createBookingDrawerOverlayProps,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import { normalizePaymentMethodForApi, paymentMethodToChipLabel } from '../../utils/paymentMethodUtils';
import { getPaymentSummaryData } from '../../utils/bookingPaymentSummary';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const PAYMENT_METHOD_OPTIONS = [
  { value: 'ONLINE', label: 'Online' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK TRANSFER', label: 'Bank Transfer' },
  { value: 'COMPANY SETTLEMENT', label: 'Company Settlement' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CREDIT SALE', label: 'Credit Sale' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'PAYMENT LINK', label: 'Payment Link' },
  { value: 'CASH DEPOSIT', label: 'Cash Deposit' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'WALLET', label: 'Wallet' },
];

function normalizeMethodForApi(method) {
  return normalizePaymentMethodForApi(method);
}

function resolvePaymentTypeLabel(amount, totalAmount, currentType) {
  const amt = parseFloat(amount);
  const total = parseFloat(totalAmount);
  if (Number.isFinite(amt) && Number.isFinite(total) && total > 0 && amt >= total) {
    return 'Full Payment';
  }
  if (currentType === 'Full Payment') {
    return 'Partial Payment';
  }
  return currentType;
}

function isEditablePaymentId(id) {
  if (id == null || id === '') return false;
  const idStr = String(id);
  if (idStr.startsWith('booking-due-') || idStr.startsWith('invoice-')) return false;
  const numeric = Number(idStr);
  return Number.isInteger(numeric) && numeric > 0;
}

function getStatusColors(status, isDark) {
  const statusUpper = String(status || '').toUpperCase();
  if (statusUpper === 'SUCCESS' || statusUpper === 'SUCCEEDED') {
    return {
      bg: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
      color: isDark ? '#81c784' : '#2e7d32',
    };
  }
  if (statusUpper === 'FAILED') {
    return {
      bg: isDark ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
      color: isDark ? '#f44336' : '#c62828',
    };
  }
  if (statusUpper === 'PENDING') {
    return {
      bg: isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
      color: isDark ? '#ff9800' : '#e65100',
    };
  }
  return {
    bg: isDark ? 'rgba(158, 158, 158, 0.2)' : 'rgba(158, 158, 158, 0.15)',
    color: isDark ? '#9e9e9e' : '#616161',
  };
}

const formatCurrency = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export default function PaymentCollectionDrawer({ open, onClose, paymentRow, onPaymentsChanged }) {
  const { can } = usePermission();
  const canViewBookingAudit = can(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const theme = useTheme();
  const drawerRootRef = useRef(null);
  const isDark = detectThemeSync() === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);

  const scopedModalSx = useMemo(
    () => ({
      position: 'absolute',
      zIndex: theme.zIndex.modal,
    }),
    [theme.zIndex.modal]
  );

  const scopedBackdropSx = useMemo(
    () => ({
      position: 'absolute',
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(0, 0, 0, 0.45)',
    }),
    [isDark]
  );

  const drawerModalContainer = () => drawerRootRef.current ?? undefined;
  const accent = BOOKING_DRAWER_ACCENT;

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    amount: '',
    payment_method: 'CASH',
    provider_txn_id: '',
    notes: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [addPaymentDrawerOpen, setAddPaymentDrawerOpen] = useState(false);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const bookingId = paymentRow?.booking_id;
  const auditBookingId = useMemo(() => {
    const parsedId = Number(bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [bookingId]);

  const fetchPayments = useCallback(async () => {
    if (!paymentRow) {
      setPayments([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        group_by_invoice: 0,
        per_page: 100,
        page: 1,
        sort: '-payment_date',
      };

      if (paymentRow.booking_code && paymentRow.booking_code !== '—') {
        params.booking_code = paymentRow.booking_code;
      } else if (paymentRow.invoice_number && paymentRow.invoice_number !== '—') {
        params.invoice_number = paymentRow.invoice_number;
      }

      const response = await getPaymentList(params);
      const rows = Array.isArray(response?.Data) ? response.Data : [];
      const editableRows = rows.filter((row) => isEditablePaymentId(row.id));
      setPayments(
        editableRows.length > 0
          ? editableRows
          : (isEditablePaymentId(paymentRow?.id) ? [paymentRow] : []),
      );
    } catch {
      setPayments(isEditablePaymentId(paymentRow?.id) ? [paymentRow] : []);
    } finally {
      setLoading(false);
    }
  }, [paymentRow]);


  useEffect(() => {
    if (open && paymentRow) {
      fetchPayments();
    } else if (!open) {
      setPayments([]);
      setEditingPaymentId(null);
      setDeleteDialog({ open: false, payment: null });
      setDeleteConfirmText('');
      setAddPaymentDrawerOpen(false);
      setAuditDrawerOpen(false);
    }
  }, [open, paymentRow, fetchPayments]);

  const summary = useMemo(() => {
    const fromApi = paymentRow?.booking_payment_summary;
    const pseudoBooking = {
      payment_summary: fromApi,
      total_amount: paymentRow?.total_amount,
      currency: fromApi?.currency || paymentRow?.currency,
    };
    const base = getPaymentSummaryData(pseudoBooking, payments);
    const paidFromPayments = payments.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0),
      0,
    );
    const hasLoadedPayments = payments.length > 0;
    const paidAmount = hasLoadedPayments
      ? paidFromPayments
      : base.paidAmount;
    const resolvedTotal = base.totalAmount > 0 ? base.totalAmount : paidAmount;
    const remainingAmount = Math.max(0, resolvedTotal - paidAmount);
    const returnAmount = Math.max(0, paidAmount - resolvedTotal);

    return {
      ...base,
      totalAmount: resolvedTotal,
      paidAmount,
      remainingAmount,
      returnAmount,
      displayTotal: resolvedTotal,
    };
  }, [paymentRow, payments]);


  const startEdit = (payment) => {
    setEditingPaymentId(payment.id);
    setEditDraft({
      amount: String(payment.amount ?? ''),
      payment_method: paymentMethodToChipLabel(payment.payment_method),
      provider_txn_id: payment.reference_number || payment.provider_txn_id || '',
      notes: payment.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditingPaymentId(null);
    setEditDraft({ amount: '', payment_method: 'CASH', provider_txn_id: '', notes: '' });
  };

  const handleSaveEdit = async () => {
    if (!bookingId || !editingPaymentId) return;
    const amount = parseFloat(editDraft.amount);
    if (!Number.isFinite(amount) || amount < 0.01) {
      setSnackbar({ open: true, message: 'Enter a valid amount (minimum ₹0.01).', severity: 'error' });
      return;
    }

    setSavingEdit(true);
    try {
      await updateBooking(bookingId, {
        update_payments: [
          {
            id: Number(editingPaymentId),
            amount,
            payment_method: editDraft.payment_method,
            provider_txn_id: editDraft.provider_txn_id || '',
            notes: editDraft.notes || '',
          },
        ],
      });
      setPayments((prev) =>
        prev.map((p) =>
          String(p.id) === String(editingPaymentId)
            ? {
                ...p,
                amount,
                payment_method: editDraft.payment_method,
                payment_type: resolvePaymentTypeLabel(amount, summary.totalAmount, p.payment_type),
                reference_number: editDraft.provider_txn_id || p.reference_number,
                provider_txn_id: editDraft.provider_txn_id || '',
                notes: editDraft.notes || '',
              }
            : p,
        ),
      );
      setSnackbar({ open: true, message: 'Payment updated successfully.', severity: 'success' });
      cancelEdit();
      await fetchPayments();
      onPaymentsChanged?.();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to update payment.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePayment = async () => {
    const payment = deleteDialog.payment;
    if (!payment?.id || !bookingId) return;
    if (deleteConfirmText.trim().toLowerCase() !== 'confirm') return;

    setDeletingPayment(true);
    try {
      await updateBooking(bookingId, { delete_payments: [Number(payment.id)] });
      setPayments((prev) => prev.filter((p) => String(p.id) !== String(payment.id)));
      setSnackbar({ open: true, message: 'Payment deleted successfully.', severity: 'success' });
      setDeleteDialog({ open: false, payment: null });
      setDeleteConfirmText('');
      if (String(editingPaymentId) === String(payment.id)) cancelEdit();
      await fetchPayments();
      onPaymentsChanged?.();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to delete payment.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setDeletingPayment(false);
    }
  };

  const handlePaymentAdded = async () => {
    setSnackbar({ open: true, message: 'Payment added successfully.', severity: 'success' });
    await fetchPayments();
    onPaymentsChanged?.();
  };

  const { selectMenuProps } = useMemo(
    () => createBookingDrawerOverlayProps(isDark),
    [isDark],
  );

  if (!paymentRow) return null;

  const canMutate = Boolean(bookingId);
  const deleteConfirmValid = deleteConfirmText.trim().toLowerCase() === 'confirm';
  const deleteTarget = deleteDialog.payment;
  const deleteTargetDate = deleteTarget?.processed_at || deleteTarget?.payment_date || deleteTarget?.created_at;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          ...getBookingDrawerPaperSx(isDark),
          width: BOOKING_DRAWER_WIDTH_SX,
        },
      }}
    >
      <Box
        ref={drawerRootRef}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
          <Box
            sx={{
              ...getBookingDrawerHeaderSx(isDark),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
            }}
          >
            <Box>
              <PaymentDrawerHeaderTitle
                title="Payment collection"
                booking={{
                  id: paymentRow.booking_id,
                  booking_id: paymentRow.booking_id,
                  invoice_number: paymentRow.invoice_number,
                  booking_code: paymentRow.booking_code,
                  guest_name: paymentRow.guest_name,
                }}
                bookingId={paymentRow.booking_id}
                paymentHistory={payments}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={accent}
              />
            </Box>
            {canViewBookingAudit && (
              <Tooltip title={auditBookingId ? 'Open booking audit log' : 'Booking audit unavailable'}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setAuditDrawerOpen(true)}
                    disabled={!auditBookingId}
                    sx={{
                      ...getBookingDrawerAuditButtonSx(isDark),
                      minWidth: 'auto',
                      height: { xs: 32, sm: 34 },
                      minHeight: { xs: 32, sm: 34 },
                      px: { xs: 1.1, sm: 1.5 },
                      py: 0.3,
                      fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                      '& .MuiButton-startIcon': {
                        mr: { xs: 0.45, sm: 0.75 },
                      },
                    }}
                  >
                    Audit
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25, color: drawerTheme.text }}>
                Payment summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                <SummaryItem label="Booking code" value={paymentRow.booking_code || '—'} drawerTheme={drawerTheme} highlight={false} />
                <SummaryItem label="Name" value={paymentRow.guest_name || '—'} drawerTheme={drawerTheme} highlight={false} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <SummaryItem label="Invoice ID" value={paymentRow.invoice_number || '—'} drawerTheme={drawerTheme} highlight={false} />
                <SummaryItem label="Total amount" value={formatCurrency(summary.totalAmount, summary.currency)} drawerTheme={drawerTheme} highlight={false} />
                <SummaryItem label="Total paid" value={formatCurrency(summary.paidAmount, summary.currency)} drawerTheme={drawerTheme} highlight />
                <SummaryItem label="Remaining" value={formatCurrency(summary.remainingAmount, summary.currency)} drawerTheme={drawerTheme} highlight={false} />
              </Box>
            </Paper>

            <BookingOverpaidBanner
              returnAmount={summary.returnAmount}
              paidAmount={summary.paidAmount}
              totalAmount={summary.totalAmount}
              currency={summary.currency}
              isDark={isDark}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: drawerTheme.text }}>
                Payment history
                {payments.length > 0 ? (
                  <Chip label={payments.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                ) : null}
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ color: accent }} />
                </Box>
              ) : payments.length === 0 ? (
                <Typography variant="body2" sx={{ color: drawerTheme.subtleText, textAlign: 'center', py: 3 }}>
                  No payment history found
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {payments.map((payment, index) => {
                    
                    const isEditing = String(editingPaymentId) === String(payment.id);
                    const isActive =
                      !paymentRow?.is_invoice_summary &&
                      payment.id != null &&
                      paymentRow?.id != null &&
                      String(payment.id) === String(paymentRow.id);
                    const paymentDate = payment.processed_at || payment.payment_date || payment.created_at;
                    const paymentStatus = payment.payment_status || payment.payment_status_value || 'SUCCESS';
                    const statusColors = getStatusColors(paymentStatus, isDark);
                    const editable = canMutate && isEditablePaymentId(payment.id);

                    return (
                      <Fade in key={payment.id || index} timeout={280}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: '12px',
                            backgroundColor: isEditing
                              ? (isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.08)')
                              : (isActive
                                ? (isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.06)')
                                : (isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)')),
                            border: `1px solid ${isEditing ? accent : (isActive ? `${accent}80` : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'))}`,
                            boxShadow: isEditing ? `0 0 0 1px ${accent}40, 0 8px 24px rgba(76, 225, 182, 0.12)` : 'none',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: drawerTheme.text, fontSize: '0.95rem' }}>
                                {formatCurrency(payment.amount || 0, payment.currency || summary.currency)}
                              </Typography>
                              {paymentDate ? (
                                <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.7rem' }}>
                                  {formatHotelDateTime(paymentDate)}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <Chip
                                label={paymentStatus}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  backgroundColor: statusColors.bg,
                                  color: statusColors.color,
                                  fontWeight: 600,
                                  border: `1.5px solid ${statusColors.color}60`,
                                }}
                              />
                              {payment.payment_method ? (
                                <Chip
                                  icon={<PaymentIcon sx={{ fontSize: 12 }} />}
                                  label={payment.payment_method}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                    color: accent,
                                    fontWeight: 600,
                                    border: `1px solid ${accent}40`,
                                  }}
                                />
                              ) : null}
                            </Box>
                          </Box>

                          {!isEditing && (payment.payment_type && payment.payment_type !== '—') || payment.reference_number || payment.provider_txn_id ? (
                            <>
                              <Divider sx={{ my: 0.75, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                              <Stack spacing={0.5}>
                                {payment.payment_type && payment.payment_type !== '—' ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <DetailRow label="Type" value={payment.payment_type} drawerTheme={drawerTheme} isDark={isDark} />
                                    {editable ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                        <Tooltip title="Edit payment">
                                          <IconButton
                                            size="small"
                                            onClick={() => startEdit(payment)}
                                            sx={{ color: accent, border: `1px solid ${accent}40`, borderRadius: '8px' }}
                                          >
                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete payment">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setDeleteDialog({ open: true, payment });
                                              setDeleteConfirmText('');
                                            }}
                                            sx={{ color: '#f44336', border: '1px solid rgba(244,67,54,0.35)', borderRadius: '8px' }}
                                          >
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    ) : null}
                                  </Box>
                                ) : null}
                                {payment.reference_number || payment.provider_txn_id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <DescriptionIcon sx={{ fontSize: 14, color: drawerTheme.subtleText }} />
                                    <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.7rem', minWidth: 72 }}>
                                      Reference:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: drawerTheme.text, fontSize: '0.7rem', fontWeight: 600, fontFamily: 'monospace' }}>
                                      {payment.reference_number || payment.provider_txn_id}
                                    </Typography>
                                  </Box>
                                ) : null}
                              </Stack>
                            </>
                          ) : null}

                          <Collapse in={isEditing} timeout={300} unmountOnExit>
                            <Box
                              sx={{
                                mt: 1.25,
                                pt: 1.25,
                                borderTop: `1px dashed ${isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.35)'}`,
                              }}
                            >
                              <Typography variant="caption" sx={{ color: accent, fontWeight: 700, letterSpacing: 0.4, mb: 1, display: 'block' }}>
                                EDIT PAYMENT
                              </Typography>
                              <Stack spacing={1.25}>
                                <TextField
                                  label="Amount"
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={editDraft.amount}
                                  onChange={(e) => setEditDraft((prev) => ({ ...prev, amount: e.target.value }))}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                  }}
                                  inputProps={{ min: 0.01, step: 0.01 }}
                                />
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Payment method</InputLabel>
                                  <Select
                                    label="Payment method"
                                    value={editDraft.payment_method}
                                    MenuProps={selectMenuProps}
                                    onChange={(e) => setEditDraft((prev) => ({ ...prev, payment_method: e.target.value }))}
                                  >
                                    {PAYMENT_METHOD_OPTIONS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  label="Reference number"
                                  size="small"
                                  fullWidth
                                  value={editDraft.provider_txn_id}
                                  onChange={(e) => setEditDraft((prev) => ({ ...prev, provider_txn_id: e.target.value }))}
                                />
                                <TextField
                                  label="Notes"
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  value={editDraft.notes}
                                  onChange={(e) => setEditDraft((prev) => ({ ...prev, notes: e.target.value }))}
                                />
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CancelOutlinedIcon />}
                                    onClick={cancelEdit}
                                    disabled={savingEdit}
                                    sx={{ borderRadius: '10px', textTransform: 'none' }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={savingEdit ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                                    onClick={handleSaveEdit}
                                    disabled={savingEdit}
                                    sx={{
                                      borderRadius: '10px',
                                      textTransform: 'none',
                                      bgcolor: accent,
                                      color: '#02221b',
                                      '&:hover': { bgcolor: '#3dd4ab' },
                                    }}
                                  >
                                    {savingEdit ? 'Saving…' : 'Save changes'}
                                  </Button>
                                </Stack>
                              </Stack>
                            </Box>
                          </Collapse>
                        </Paper>
                      </Fade>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>

          <Box sx={{ p: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                sx={{
                  ...getBookingDrawerOutlinedButtonSx(isDark),
                  textTransform: 'none',
                  borderRadius: '10px',
                  py: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={!canMutate}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={() => setAddPaymentDrawerOpen(true)}
                sx={{
                  ...getBookingDrawerPrimaryButtonSx(isDark),
                  textTransform: 'none',
                  borderRadius: '10px',
                  py: 1,
                }}
              >
                Add Payment
              </Button>
            </Stack>
          </Box>

        <BookingAddPaymentDrawer
          open={addPaymentDrawerOpen}
          onClose={() => setAddPaymentDrawerOpen(false)}
          bookingId={bookingId}
          bookingContext={{
            booking_code: paymentRow.booking_code,
            invoice_number: paymentRow.invoice_number,
            guest_name: paymentRow.guest_name,
          }}
          paymentHistory={payments}
          isDark={isDark}
          onPaymentAdded={handlePaymentAdded}
        />

        <Dialog
          open={deleteDialog.open}
          onClose={() => !deletingPayment && setDeleteDialog({ open: false, payment: null })}
          container={drawerModalContainer}
          sx={scopedModalSx}
          BackdropProps={{ sx: scopedBackdropSx }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: isDark ? drawerTheme.surface : '#fff',
              border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.22)' : 'rgba(76, 225, 182, 0.18)'}`,
              boxShadow: isDark
                ? '0 16px 36px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(76, 225, 182, 0.1)'
                : '0 12px 28px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(76, 225, 182, 0.08)',
              minWidth: 260,
              maxWidth: 'calc(100% - 24px)',
              m: 1.5,
            },
          }}
        >
          <Box
            sx={{
              height: 3,
              background: `linear-gradient(90deg, ${accent}66 0%, ${accent} 50%, ${accent}66 100%)`,
            }}
          />
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 1.5,
              pb: 0.5,
              px: 2,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: isDark ? 'rgba(244, 67, 54, 0.14)' : 'rgba(244, 67, 54, 0.08)',
                border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.35)' : 'rgba(244, 67, 54, 0.22)'}`,
              }}
            >
              <DeleteForeverIcon sx={{ color: '#f44336', fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: drawerTheme.text, lineHeight: 1.2, fontSize: '0.95rem' }}>
                Delete payment
              </Typography>
              <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                Permanent
              </Typography>
            </Box>
            <IconButton
              size="small"
              disabled={deletingPayment}
              onClick={() => {
                setDeleteDialog({ open: false, payment: null });
                setDeleteConfirmText('');
              }}
              sx={{ color: drawerTheme.subtleText, p: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 2, pt: 0.5, pb: 0 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                mb: 1.25,
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)'}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.75}>
                <Box>
                  <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase' }}>
                    Amount
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: drawerTheme.text, lineHeight: 1.2 }}>
                    {formatCurrency(deleteTarget?.amount || 0, summary.currency)}
                  </Typography>
                  {deleteTargetDate ? (
                    <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.65rem' }}>
                      {formatHotelDateTime(deleteTargetDate)}
                    </Typography>
                  ) : null}
                </Box>
                <Stack spacing={0.5} alignItems="flex-end">
                  {deleteTarget?.payment_method ? (
                    <Chip
                      icon={<PaymentIcon sx={{ fontSize: 12 }} />}
                      label={deleteTarget.payment_method}
                      size="small"
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: '0.68rem',
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.08)',
                        color: accent,
                        border: `1px solid ${accent}30`,
                      }}
                    />
                  ) : null}
                  {deleteTarget?.payment_type && deleteTarget.payment_type !== '—' ? (
                    <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.65rem', fontWeight: 600 }}>
                      {deleteTarget.payment_type}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>

            <Alert
              severity="warning"
              icon={<WarningAmberRoundedIcon sx={{ fontSize: 18 }} />}
              variant="outlined"
              sx={{
                py: 0.25,
                px: 1,
                borderRadius: '10px',
                alignItems: 'center',
                backgroundColor: isDark ? 'rgba(255, 152, 0, 0.06)' : 'rgba(255, 152, 0, 0.04)',
                borderColor: isDark ? 'rgba(255, 152, 0, 0.28)' : 'rgba(255, 152, 0, 0.28)',
                '& .MuiAlert-icon': { mr: 0.75, py: 0.5 },
                '& .MuiAlert-message': { color: drawerTheme.text, fontSize: '0.75rem', lineHeight: 1.4, py: 0.5 },
              }}
            >
              Cannot be undone. Recorded in the booking audit log.
            </Alert>

            <Box sx={{ mt: 1.25 }}>
              <Typography variant="caption" sx={{ color: drawerTheme.subtleText, mb: 0.75, display: 'block' }}>
                Type{' '}
                <Box
                  component="span"
                  sx={{
                    px: 0.5,
                    py: 0.1,
                    mx: 0.25,
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: deleteConfirmValid ? accent : drawerTheme.text,
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.08)',
                    border: `1px solid ${deleteConfirmValid ? `${accent}80` : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
                  }}
                >
                  confirm
                </Box>
                to proceed
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Type confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                autoFocus
                error={deleteConfirmText.length > 0 && !deleteConfirmValid}
                disabled={deletingPayment}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: deleteConfirmValid ? accent : drawerTheme.subtleText,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
                    '& fieldset': {
                      borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    },
                    '&:hover fieldset': {
                      borderColor: `${accent}55`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: deleteConfirmValid ? accent : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'),
                    },
                  },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 2, pb: 1.5, pt: 0.5, gap: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDeleteDialog({ open: false, payment: null });
                setDeleteConfirmText('');
              }}
              disabled={deletingPayment}
              sx={{
                flex: 1,
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 600,
                color: drawerTheme.text,
                borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)',
              }}
            >
              Keep
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={deletingPayment || !deleteConfirmValid}
              onClick={handleDeletePayment}
              startIcon={
                deletingPayment ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DeleteForeverIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                flex: 1,
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 700,
                boxShadow: deleteConfirmValid && !deletingPayment ? '0 4px 12px rgba(244, 67, 54, 0.3)' : 'none',
              }}
            >
              {deletingPayment ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: theme.zIndex.snackbar,
          }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {canViewBookingAudit && (
          <BookingAuditDrawer
            open={auditDrawerOpen}
            onClose={() => setAuditDrawerOpen(false)}
            bookingId={auditBookingId}
            bookingCode={paymentRow?.booking_code}
            isDark={isDark}
          />
        )}
      </Box>
    </Drawer>
  );
}

function SummaryItem({ label, value, drawerTheme, highlight = false }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: highlight ? 700 : 600,
          color: highlight ? BOOKING_DRAWER_ACCENT : drawerTheme.text,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DetailRow({ label, value, isDark, drawerTheme }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.7rem', minWidth: 72 }}>
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.text : '#333', fontSize: '0.7rem', fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}
